import { useScrollTo } from "../../hooks/useScrollTo";
import {
  CashRegistryDetails,
  Configuration,
  RegistryDetails,
  Section,
  SectionTypes,
} from "../../resources/modelTypes";
import { DisplayPhoto } from "./DisplayPhoto";
import { DisplayAccommodationSection } from "./section-types/DisplayAccommodationSection";
import { DisplayBirdieCashRegistrySection } from "./section-types/DisplayBirdieCashRegistrySection";
import { DisplayButtonSection } from "./section-types/DisplayButtonSection";
import { DisplayCommentSection } from "./section-types/DisplayCommentSection";
import { DisplayDividerSection } from "./section-types/DisplayDividerSection";
import { DisplayFAQSection } from "./section-types/DisplayFAQSection";
import { DisplayGallerySection } from "./section-types/DisplayGallerySection";
import { DisplayImageAndTextSection } from "./section-types/DisplayImageAndTextSection";
import { DisplayLocationSection } from "./section-types/DisplayLocationSection";
import { DisplayStripeRegistrySection } from "./section-types/DisplayStripeRegistrySection";
import { DisplayTextSection } from "./section-types/DisplayTextSection";
import { DisplayWebsiteUrlSection } from "./section-types/DisplayWebsiteUrlSection";
import { RegistryCard } from "./section-types/RegistryCard";
import React, { memo, useEffect } from "react";

export type DisplaySectionWebsiteProps = {
  configuration: Configuration;
  section: Section;
};

const DisplaySection = ({
  configuration,
  section,
}: DisplaySectionWebsiteProps) => {
  const sectionType = section.sectionType;
  const { scrollToId } = useScrollTo();

  // Scrolls to newly created section on initial render
  useEffect(() => {
    if (!section.id) {
      scrollToId("section-new");
    }
  }, [section.id, scrollToId]);

  switch (sectionType) {
    case SectionTypes.Anchor:
      return <DisplayWebsiteUrlSection section={section} />;
    case SectionTypes.Button:
      return <DisplayButtonSection section={section} />;
    case SectionTypes.Divider:
      return <DisplayDividerSection />;
    case SectionTypes.Gallery:
      return <DisplayGallerySection section={section} />;
    case SectionTypes.ImageAndText:
      return (
        <DisplayImageAndTextSection
          configuration={configuration}
          section={section}
        />
      );
    case SectionTypes.Location:
      return <DisplayLocationSection section={section} />;
    case SectionTypes.Media:
      return <DisplayPhoto configuration={configuration} section={section} />;
    case SectionTypes.Registry:
      return (
        <RegistryCard
          configuration={configuration}
          details={section.sectionDetails as RegistryDetails}
          section={section}
        />
      );
    case SectionTypes.Text:
      return <DisplayTextSection section={section} />;
    case SectionTypes.Accommodation:
      return (
        <DisplayAccommodationSection
          configuration={configuration}
          section={section}
        />
      );
    case SectionTypes.CashRegistry:
      return (section.sectionDetails as CashRegistryDetails)?.cashRegistryId ? (
        <DisplayBirdieCashRegistrySection section={section} />
      ) : (
        <RegistryCard
          configuration={configuration}
          details={section.sectionDetails as RegistryDetails}
          section={section}
        />
      );
    case SectionTypes.FAQ:
      return <DisplayFAQSection section={section} />;
    case SectionTypes.Comments:
      return <DisplayCommentSection />;
    case SectionTypes.StripeRegistry:
      return (
        <DisplayStripeRegistrySection
          configuration={configuration}
          section={section}
        />
      );
    default:
      return <div>No matching section type</div>;
  }
};

const MemoizedDisplaySectionWebsite = memo(DisplaySection);

export { MemoizedDisplaySectionWebsite as DisplaySectionWebsite };
